import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { ICountriesPayload, IGendersPayload, IHeaderInitialState } from "../types";

const initialState: IHeaderInitialState = {
    isMood: false,
    clickedRowKey: null,
    themeMode: "light",
    countries: [],
    filterCountries: [],
    genders: [],
    regions: [],
    residenceRegions: [],
    lastGlobalSearchQuery: "",
    documentsType: [],
};

export const headerSlice = createSlice({
    name: "header",
    initialState,
    reducers: {
        changeMood: (state, action: PayloadAction<boolean | undefined>) => {
            state.isMood = action.payload !== undefined ? action.payload : !state.isMood;
            state.themeMode = state.isMood ? "dark" : "light";
        },

        setClickedRowKey: (state, action) => {
            state.clickedRowKey = action.payload;
        },

        setGenders: (
            state,
            action: PayloadAction<{
                data: IGendersPayload[];
                defaultLanguage: string;
            }>
        ) => {
            const { data } = action.payload;
            state.genders = data;
        },

        setLastGlobalSearchQuery: (state, action: PayloadAction<string>) => {
            state.lastGlobalSearchQuery = action.payload;
        },

        setCountries: (
            state,
            action: PayloadAction<{
                data: ICountriesPayload[];
                defaultLanguage: string;
            }>
        ) => {
            const { data, defaultLanguage } = action.payload;
            state.countries = data;
            state.filterCountries = data.flatMap(data =>
                data.translations
                    .filter(elem => elem.slug === defaultLanguage && elem.name)
                    .map(elem => ({
                        id: data.id,
                        name: elem.name as string,
                    }))
            );
        },
        setFilteredCountries: (
            state,
            action: PayloadAction<{
                defaultLanguage: string;
            }>
        ) => {
            const { defaultLanguage } = action.payload;

            state.filterCountries = state.countries.flatMap(data =>
                data.translations
                    .filter(elem => elem.slug === defaultLanguage && elem.name)
                    .map(elem => ({
                        id: data.id,
                        name: elem.name as string,
                    }))
            );
        },
        setFilteredCountry: (state, action: PayloadAction<{ selectedLanguage: string }>) => {
            const { selectedLanguage } = action.payload;

            state.filterCountries = state.countries.flatMap(data =>
                data.translations
                    .filter(elem => elem.slug === selectedLanguage && elem.name)
                    .map(elem => ({
                        id: data.id,
                        name: elem.name as string,
                    }))
            );
        },

        setSelectedRegions: (state, action: PayloadAction<{ id: number; defaultLanguage: string }>) => {
            const { id } = action.payload;
            const selectedCountry = state.countries.find(country => country.id === id);
            if (!selectedCountry) {
                state.regions = [];
                return;
            }

            state.regions = selectedCountry.regions;
        },
        setSelectedResidenceRegions: (state, action: PayloadAction<{ id: number; defaultLanguage: string }>) => {
            const { id } = action.payload;
            const selectedCountry = state.countries.find(country => country.id === id);
            if (!selectedCountry) {
                state.residenceRegions = [];
                return;
            }
            state.residenceRegions = selectedCountry.regions;
        },
        setDocumentsType: (state, action) => {
            state.documentsType = action.payload;
        },
        logOut: state => {
            localStorage.clear();
            state.isMood = false;
            Cookies.remove("token");
        },
    },
});
export const {
    changeMood,
    logOut,
    setClickedRowKey,
    setSelectedResidenceRegions,
    setFilteredCountry,
    setSelectedRegions,
    setGenders,
    setCountries,
    setLastGlobalSearchQuery,
    setFilteredCountries,
    setDocumentsType,
} = headerSlice.actions;
